import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BokkzIbDataService, IncomeTaxEntity, Rekening} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../gedeelde-componenten/gedeelde-componenten.component';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {SharedUiPanelModule} from '@snelstart/ui-panel';
import {SharedUiIconModule} from '@snelstart/ui-icon';

type BeleggingsRekeningFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  rekeningNummer: FormControl<string | null>;
  landCode: FormControl<string | null>;
  groenRekening: FormControl<boolean | null>;
  directRendement: FormControl<number | null>;
  valutaResultaat: FormControl<number | null>;
  stortingen: FormControl<number | null>;
  onttrekkingen: FormControl<number | null>;
  waardeEindJaar: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-box3-beleggingsrekeningen',
    templateUrl: './box3-beleggingsrekeningen.component.html',
    styleUrls: ['./box3-beleggingsrekeningen.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiDetailsModule,
        CommonModule,
        SharedUiAlertModule,
        SharedUiDialogModule,
        SharedUiTooltipModule,
        SharedUiPanelModule,
        SharedUiIconModule,
    ],
})
export class Box3BeleggingsrekeningenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public dialogVisible = false;
    public index = 0;
    public jaar = 2024;
    public heeftOWR = false;
    public labelJaar = '';
    public labelJaarEind = '';

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

    public formGroup = new FormGroup({
        box3Beleggingen: new FormArray<BeleggingsRekeningFormGroup>([]),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
            this.formGroup.reset({}, { emitEvent: false });
            this.updateForm(entity?.aangifte?.box3?.beleggingsrekeningen || []);
            this.heeftOWR = entity.aangifte?.box3?.berekenWerkelijkRendement ?? false;
            this.labelJaar = entity.aangifte?.aangifteJaar ? `Waarde 1-1-${entity.aangifte.aangifteJaar}` : 'Waarde 1-1';
            this.labelJaarEind = entity.aangifte?.aangifteJaar ? `Waarde 31-12-${entity.aangifte.aangifteJaar}` : 'Saldo 31-12';
            if (entity.year !== undefined) {
                this.jaar = entity.year;
            }
        });

        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractInputForCalculation = () =>
            this.box3Beleggingen.getRawValue().map((item) => ({
                bedrag: item.bedrag,
                directRendement: item.directRendement,
                valutaResultaat: item.valutaResultaat,
                stortingen: item.stortingen,
                onttrekkingen: item.onttrekkingen,
                waardeEindJaar: item.waardeEindJaar,
            }));

        this.changeSubscriptions.add(
            this.box3Beleggingen.valueChanges
                .pipe(
                    debounceTime(500),
                    map(() => this.saveForm()),
                    map(() => extractInputForCalculation()),
                    startWith(extractInputForCalculation()),
                    pairwise(),
                    filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(() => {
                    this.dataService.calculateLatestIncomeTaxReturn().subscribe();
                }),
        );
    }

    showDialog(index: number): void {
        this.index = index;
        this.dialogVisible = true;
    }

    get box3Beleggingen(): FormArray<BeleggingsRekeningFormGroup> {
        return this.formGroup.get('box3Beleggingen') as FormArray<BeleggingsRekeningFormGroup>;
    }

    addRij(): void {
        this.box3Beleggingen.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRij(index: number): void {
        this.box3Beleggingen.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
        this.subscribeToChanges();
    }

    createFormGroup(belegging?: Rekening): BeleggingsRekeningFormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(belegging?.omschrijving ?? null),
            bedrag: new FormControl<number | null>(belegging?.bedrag ?? null),
            rekeningNummer: new FormControl<string | null>(belegging?.rekeningNummer ?? null),
            landCode: new FormControl<string | null>(belegging?.landCode ?? 'NLD'),
            groenRekening: new FormControl<boolean | null>(belegging?.groenRekening ?? false),
            directRendement: new FormControl<number | null>(belegging?.directRendement ?? null),
            valutaResultaat: new FormControl<number | null>(belegging?.valutaResultaat ?? null),
            stortingen: new FormControl<number | null>(belegging?.stortingen ?? null),
            onttrekkingen: new FormControl<number | null>(belegging?.onttrekkingen ?? null),
            waardeEindJaar: new FormControl<number | null>(belegging?.waardeEindJaar ?? null),
        });
    }

    private updateForm(data: Rekening[]): void {
        const lijst = data || [];
        const formArray = this.box3Beleggingen;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i);
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                formArray.push(this.createFormGroup(lijst[i]), { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity({ emitEvent: false });

        const items: Rekening[] = this.formGroup
            .getRawValue()
            .box3Beleggingen.filter(
                (item) =>
                    item.bedrag !== 0 &&
                    !(item.bedrag == null && item.omschrijving == null && item.rekeningNummer == null && item.landCode == null),
            )
            .map((item) => ({
                omschrijving: item.omschrijving ?? undefined,
                bedrag: item.bedrag ?? undefined,
                rekeningNummer: item.rekeningNummer ?? undefined,
                landCode: item.landCode ?? 'NLD',
                groenRekening: item.groenRekening ?? undefined,
                directRendement: item.directRendement ?? undefined,
                valutaResultaat: item.valutaResultaat ?? undefined,
                stortingen: item.stortingen ?? undefined,
                onttrekkingen: item.onttrekkingen ?? undefined,
                waardeEindJaar: item.waardeEindJaar ?? undefined,
            }));

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    box3: {
                        ...currentEntity.aangifte.box3,
                        beleggingsrekeningen: items,
                    },
                },
            } as IncomeTaxEntity;
        });

    }

    getOWRDialogTitel(i: number): string {
        const omschrijving = this.box3Beleggingen.controls[i]?.get('omschrijving')?.value;
        const rekeningnummer = this.box3Beleggingen.controls[i]?.get('rekeningNummer')?.value;

        if (!omschrijving || !rekeningnummer) {
            return 'Werkelijk rendement belegging';
        }

        return `Werkelijk rendement: ${omschrijving} - ${rekeningnummer}`;
    }

    getBerekendeWerkelijkRendement(i: number): any {
        const control = this.box3Beleggingen.at(i);
        const bedrag = Number(control?.get('bedrag')?.value) || 0;
        const directRendement = Number(control?.get('directRendement')?.value) || 0;
        const valutaResultaat = Number(control?.get('valutaResultaat')?.value) || 0;
        const stortingen = Number(control?.get('stortingen')?.value) || 0;
        const onttrekkingen = Number(control?.get('onttrekkingen')?.value) || 0;
        const waardeEindJaar = Number(control?.get('waardeEindJaar')?.value) || 0;

        if (
            !control?.get('directRendement')?.value &&
            !control?.get('valutaResultaat')?.value &&
            !control?.get('bedrag')?.value &&
            !control?.get('stortingen')?.value &&
            !control?.get('onttrekkingen')?.value &&
            !control?.get('waardeEindJaar')?.value
        ) {
            return null;
        }

        return directRendement + valutaResultaat + waardeEindJaar - bedrag + onttrekkingen - stortingen;
    }

    getGroen(i: number): string {
        const control = this.box3Beleggingen.at(i);
        const isGroenRekening = control?.get('groenRekening')?.value || false;

        return isGroenRekening ? '(voor toepassen vrijstelling groene beleggingen)' : '';
    }
}
